import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useProducts } from "@/lib/products";
import Reveal from "@/components/Reveal";

export const PATHWAYS = [
  { cat: "Metabolic Pathways", text: "Incretin-class and metabolic research peptides with clear size ladders." },
  { cat: "Cell Migration Studies", text: "Tissue-repair and cell-migration model compounds, single and blended." },
  { cat: "Peptide Signaling", text: "GH-axis secretagogues and releasing-hormone analogs for signaling work." },
  { cat: "Cellular Aging Models", text: "Compounds commonly grouped for senescence and mitochondrial research." },
  { cat: "Neuro-Signaling", text: "Neuropeptide analogs for in-vitro neuro-signaling investigation." },
  { cat: "GPCR Compounds", text: "Receptor-ligand reference compounds for GPCR binding assays." },
  { cat: "Supplies", text: "Bacteriostatic water and reconstitution reagents for the bench." },
];

export default function Pathways() {
  const products = useProducts();
  const counts = products.reduce((m, p) => ((m[p.category] = (m[p.category] || 0) + 1), m), {});

  return (
    <section id="pathways" className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28" data-testid="pathways-section">
      <Reveal className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-end mb-12">
        <div>
          <p className="font-tech text-xs uppercase tracking-[0.18em] text-sky-700 mb-3">Pathways</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Seven focused groupings,<br /><span className="italic font-medium text-slate-500">built for serious benches.</span>
          </h2>
        </div>
        <div className="flex flex-col sm:flex-row lg:flex-col sm:items-end lg:items-start gap-4 justify-between">
          <p className="text-base text-slate-600 leading-relaxed max-w-md">
            Browse by research pathway. Every grouping is informational and research-use only — pick a lane, then narrow by size, stock and COA status.
          </p>
          <Link to="/catalog" data-testid="pathways-view-all-link"
            className="inline-flex items-center gap-2 border border-slate-300 hover:border-sky-700 hover:text-sky-700 text-slate-700 font-medium text-sm px-5 py-2.5 rounded-full transition-colors whitespace-nowrap">
            View all {products.length || ""} SKUs <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-slate-200">
        {PATHWAYS.map((p, i) => (
          <Reveal key={p.cat} delay={i * 0.05}>
            <Link to={`/catalog?cat=${encodeURIComponent(p.cat)}`} data-testid={`pathway-card-0${i + 1}`}
              className="group relative block h-full border-r border-b border-slate-200 bg-white hover:bg-slate-50 p-6 sm:p-7 transition-colors">
              <span className="absolute left-0 top-0 h-0 w-px bg-sky-700 group-hover:h-full transition-all duration-500" />
              <div className="flex items-center justify-between">
                <p className="font-tech text-xs text-slate-400">0{i + 1}</p>
                <p className="font-tech text-[10px] uppercase tracking-[0.16em] text-slate-400">{counts[p.cat] ? `${counts[p.cat]} SKUs` : ""}</p>
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold text-slate-900 mt-5 leading-tight">{p.cat}</h3>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed min-h-[40px]">{p.text}</p>
              <p className="mt-6 inline-flex items-center gap-1.5 font-tech text-[11px] uppercase tracking-[0.18em] text-slate-700 group-hover:text-sky-700 transition-colors">
                Review <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </p>
            </Link>
          </Reveal>
        ))}
        <Reveal delay={0.35}>
          <Link to="/coa-library" data-testid="pathway-card-coa" className="group block h-full border-r border-b border-slate-200 bg-[#0B111A] text-white p-6 sm:p-7 hover:bg-[#111827] transition-colors">
            <p className="font-tech text-xs text-slate-500">08</p>
            <h3 className="font-display text-xl sm:text-2xl font-semibold mt-5 leading-tight">COA Library</h3>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed min-h-[40px]">Every lot, searchable, with the original analytical PDF.</p>
            <p className="mt-6 inline-flex items-center gap-1.5 font-tech text-[11px] uppercase tracking-[0.18em] text-emerald-400">
              Open <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </p>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
